import React, { useCallback, useEffect, useState } from "react";

import { assignCreditsToPartner, getAllPartners, getAssignableFarmFieldModules } from "services";

import { calculateVerificationPointsSum, truncateCredits, useCancelAsyncOnUnmount } from "utils";

import { AdminHead, AdminTable, MainPage, SkeletonWrapper, Tabs, Message as message } from "components";

import { ModulesAssignmentDrawer } from "pages/admin/partners/components/ModulesAssignmentDrawer";

const itemsPerPage = 250;

export const AssignModuleCredits = () => {
  const [currentFarmFieldModule, setCurrentFarmFieldModule] = useState(null);
  const [currentPartner, setCurrentPartner] = useState(null);
  const [farmFieldModules, setFarmFieldModules] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingAll, setLoadingAll] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showModuleAssignmentDrawer, setShowModuleAssignmentDrawer] = useState(false);
  const [quantity, setQuantity] = useState({});
  const [assignableModules, setAssignableModules] = useState([]);
  const signal = useCancelAsyncOnUnmount()();
  const [modulePage, setModulePage] = useState(0);

  const form = React.createRef();

  const loadFarmFieldModules = useCallback(() => {
    getAssignableFarmFieldModules({
      page: modulePage,
      itemsPerPage,
      signal,
    })
      .then((res) => {
        setFarmFieldModules((prev) => [...prev, ...res.modules]);

        if (res.count > modulePage * itemsPerPage && res.count > itemsPerPage) {
          setModulePage(modulePage + 1);
        } else {
          setLoadingAll(false);
        }
      })
      .finally(() => setLoadingInitial(false));
  }, [signal, modulePage]);

  const loadPartners = useCallback(() => {
    getAllPartners({
      signal,
    }).then((res) => {
      setPartners(res.partners.filter((partner) => !partner.disabled));
    });
  }, [signal]);

  useEffect(() => {
    const data = farmFieldModules.filter((fieldModule) =>
      [fieldModule.category1, fieldModule.category2, fieldModule.category3, fieldModule.category4].some((item) => Math.abs(item) >= 0.0001)
    );

    setAssignableModules(data);
  }, [farmFieldModules]);

  useEffect(() => {
    if (form.current) {
      form.current.setFieldsValue({
        quantity: quantity,
      });
    }
    // eslint-disable-next-line
  }, [quantity]);

  const handleCloseModuleAssignmentDrawer = () => {
    setCurrentPartner(null);
    setShowModuleAssignmentDrawer(false);
  };

  const handleOpenAssignmentDrawerModule = (fieldModule) => {
    setShowModuleAssignmentDrawer(true);

    setCurrentFarmFieldModule({ ...fieldModule, id: fieldModule.fieldModuleId });
  };

  const setCreditQuantity = (credits) => {
    const { category1, category2, category3, category4 } = credits;
    if (![category1, category2, category3, category4].includes(undefined)) {
      setQuantity({
        category1,
        category2,
        category3,
        category4,
      });
    }
  };

  const handlePartnerChange = (partnerId) => {
    const newPartner = partners.find((partner) => partner.id === partnerId);
    setCurrentPartner(newPartner);

    setQuantity({
      category1: truncateCredits(currentFarmFieldModule.category1),
      category2: truncateCredits(currentFarmFieldModule.category2),
      category3: truncateCredits(currentFarmFieldModule.category3),
      category4: truncateCredits(currentFarmFieldModule.category4),
    });
  };

  const complete = (v) => {
    const { category1, category2, category3, category4 } = quantity;
    if ([category1, category2, category3, category4].every((item) => item === 0)) {
      message({
        content: "Quantity can not be 0",
        icon: "gremlin",
      });
      return;
    }

    if (v.creditType === "Insetting" && v.partnerId !== currentFarmFieldModule.farmPartnerId) {
      message({
        content: "You can only assign credits as insetting, when the farmer is assigned to the partner",
        icon: "gremlin",
      });
      return;
    }

    setButtonLoading(true);

    assignCreditsToPartner({
      credits: quantity,
      farmFieldMethodOrModuleId: currentFarmFieldModule.id,
      partnerId: v.partnerId,
      creditType: v.creditType,
      farmId: currentFarmFieldModule.farmId,
      fieldId: currentFarmFieldModule.fieldId,
      completed: currentFarmFieldModule.completed,
      year: currentFarmFieldModule.year,
      type: "Soil+",
      signal,
    }).then(() => {
      // Update field module locally so we don't have to refetch all data
      const updatedFieldModule = {
        ...currentFarmFieldModule,
        category1: currentFarmFieldModule.category1 - quantity.category1,
        category2: currentFarmFieldModule.category2 - quantity.category2,
        category3: currentFarmFieldModule.category3 - quantity.category3,
        category4: currentFarmFieldModule.category4 - quantity.category4,
        assignedCreditsCategory1: currentFarmFieldModule.assignedCreditsCategory1 + quantity.category1,
        assignedCreditsCategory2: currentFarmFieldModule.assignedCreditsCategory2 + quantity.category2,
        assignedCreditsCategory3: currentFarmFieldModule.assignedCreditsCategory3 + quantity.category3,
        assignedCreditsCategory4: currentFarmFieldModule.assignedCreditsCategory4 + quantity.category4,
      };

      updatedFieldModule.assignedCredits =
        updatedFieldModule.assignedCreditsCategory1 +
        updatedFieldModule.assignedCreditsCategory2 +
        updatedFieldModule.assignedCreditsCategory3 +
        updatedFieldModule.assignedCreditsCategory4;

      updatedFieldModule.totalCredits =
        updatedFieldModule.category1 + updatedFieldModule.category2 + updatedFieldModule.category3 + updatedFieldModule.category4;

      const copiedFarmFieldModules = [...farmFieldModules];
      const index = copiedFarmFieldModules.findIndex(({ id, year }) => id === updatedFieldModule.id && year === updatedFieldModule.year);
      copiedFarmFieldModules[index] = updatedFieldModule;
      setFarmFieldModules(copiedFarmFieldModules);

      handleCloseModuleAssignmentDrawer();
      setCurrentFarmFieldModule(null);
      setCurrentPartner(null);
      loadPartners();
      setButtonLoading(false);
      message({
        content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
        icon: "gremlin",
      });
    });
  };

  useEffect(() => {
    loadFarmFieldModules();
  }, [loadFarmFieldModules]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const { category1, category2, category3, category4 } = quantity;
  const categorySum = category1 || 0 + category2 || 0 + category3 || 0 + category4 || 0;
  const remainingPartnerCredits = currentPartner ? currentPartner.remainingCredits - categorySum : 0;

  const remainingModuleCredits = {
    category1: currentFarmFieldModule?.category1 || 0,
    category2: currentFarmFieldModule?.category2 || 0,
    category3: currentFarmFieldModule?.category3 || 0,
    category4: currentFarmFieldModule?.category4 || 0,
  };

  if (currentFarmFieldModule && currentPartner) {
    remainingModuleCredits.category1 = currentFarmFieldModule.category1 - category1;
    remainingModuleCredits.category2 = currentFarmFieldModule.category2 - category2;
    remainingModuleCredits.category3 = currentFarmFieldModule.category3 - category3;
    remainingModuleCredits.category4 = currentFarmFieldModule.category4 - category4;
  }

  return (
    <MainPage loading={loadingInitial} pageName={"admin"}>
      <AdminHead
        headline={
          <div className="flex-center-align">
            <SkeletonWrapper isLoading={loadingAll} type="button" buttonShape="round" buttonSize="small" className="admin-head-skeleton">
              {parseInt(assignableModules.reduce((acc, assignableModule) => acc + calculateVerificationPointsSum(assignableModule), 0))}
            </SkeletonWrapper>{" "}
            unnassigned module credits
          </div>
        }
        description="You can assign module credits to partners here. The assigned credits will be subtracted from the partner's available balance. Each credit is equivalent to 1000 points or kilograms of sequestered carbon."
      />
      <Tabs
        items={[
          {
            label: "Modules",
            key: "modules",
            children: assignableModules.length > 0 && (
              <AdminTable
                dataSource={assignableModules?.map(({ id, ...rest }) => ({ fieldModuleId: id, ...rest }))}
                config={{
                  defaultPageSize: 20,
                  actions: [
                    {
                      icon: "klim/plus",
                      func: handleOpenAssignmentDrawerModule,
                      tooltip: "Assign these credits",
                    },
                  ],
                  exclude: [
                    "receivedPoints",
                    "moduleId",
                    "fieldName",
                    "assignedCredits",
                    "methodId",
                    "completed",
                    "fieldId",
                    "farmPartnerId",
                    "assignedCreditsCategory1",
                    "assignedCreditsCategory2",
                    "assignedCreditsCategory3",
                    "assignedCreditsCategory4",
                    "createdAt",
                  ],
                }}
              />
            ),
          },
          {
            label: "Partners",
            key: "partners",
            children: partners.length > 0 && (
              <AdminTable
                dataSource={partners}
                config={{
                  exclude: [
                    "remainingCredits",
                    "referralToken",
                    "pointPriceCategory1",
                    "pointPriceCategory2",
                    "pointPriceCategory3",
                    "pointPriceCategory4",
                    "maxHectares",
                    "disabled",
                    "contactPhone",
                    "contactName",
                    "contactEmail",
                  ],
                  defaultPageSize: 10,
                }}
              />
            ),
          },
        ]}
      />

      {currentFarmFieldModule && showModuleAssignmentDrawer && (
        <ModulesAssignmentDrawer
          showAssignmentDrawer={showModuleAssignmentDrawer}
          handleCloseAssignmentDrawer={handleCloseModuleAssignmentDrawer}
          form={form}
          complete={complete}
          currentFarmFieldModule={currentFarmFieldModule}
          remainingModuleCredits={remainingModuleCredits}
          handlePartnerChange={handlePartnerChange}
          currentPartner={currentPartner}
          partners={partners}
          remainingPartnerCredits={remainingPartnerCredits}
          quantity={quantity}
          setCreditQuantity={setCreditQuantity}
          type={"module"}
          loading={buttonLoading}
        />
      )}
    </MainPage>
  );
};
